/**
 * JourneyReport — GA "Purchase journey" / "Checkout journey" funnel
 * (FEAT-ANALYTICS-002). Ordered step bars (user count + % + abandonment) with a
 * breakdown table by device/channel/etc. Data: /analytics/journey (windowFunnel).
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Flex, Text, SimpleGrid, Select, HStack, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Link,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiExternalLink } from 'react-icons/fi';
import { useReport } from './api';
import { ReportFrame, EmptyState, fmtInt } from './components';
import { countryName } from './geo';

const BREAKDOWNS = [
  { value: 'device', label: 'Device' },
  { value: 'channel', label: 'Platform / channel' },
  { value: 'os', label: 'Operating system' },
  { value: 'country', label: 'Country' },
  { value: 'source', label: 'Source' },
  { value: 'channel_group', label: 'Channel group' },
];

export function JourneyReport({ params, type }) {
  const [breakdown, setBreakdown] = useState('device');
  const q = useMemo(() => ({ ...params, type, breakdown }), [params, type, breakdown]);
  const { data, loading, error } = useReport('journey', q);

  const steps = data?.steps || [];
  const maxUsers = Math.max(1, ...steps.map((s) => Number(s.users || 0)));
  const rows = data?.breakdown?.rows || [];
  const fmtSeg = (s) => (breakdown === 'country' ? countryName(s) : s || '(not set)');

  return (
    <ReportFrame loading={loading} error={error}>
      <Box bg="white" borderWidth="1px" borderColor="#ebedf0" borderRadius="14px" boxShadow="0 1px 2px rgba(16,24,40,.04)" p={{ base: 4, md: 5 }}>
        {/* Build a custom funnel in the dedicated Funnels builder (no duplication here). */}
        <Flex justify="flex-end" mb={2}>
          <Link as={RouterLink} to="/analytics/funnels" fontSize="xs" color="var(--brand-primary)" fontWeight="600" display="inline-flex" alignItems="center" gap="4px">
            Build a custom funnel <FiExternalLink />
          </Link>
        </Flex>
        {/* Steps whose events never occur for this tenant are omitted so the funnel
            doesn't collapse to a false 100% abandonment (DA-BUG-0457). */}
        {data?.dropped_steps?.length > 0 && (
          <Text fontSize="xs" color="gray.500" mb={2}>
            Omitted (no events in this period): {data.dropped_steps.join(', ')}.
          </Text>
        )}
        {/* Funnel step bars */}
        <SimpleGrid columns={{ base: 1, md: steps.length }} spacing={0} borderBottom="1px solid" borderColor="gray.100">
          {steps.map((s, i) => {
            const h = Math.round((Number(s.users || 0) / maxUsers) * 180);
            return (
              <Box key={i} px={3} pb={3} borderRight={i < steps.length - 1 ? '1px solid' : 'none'} borderColor="gray.100">
                <Flex justify="space-between" align="baseline" mb={1}>
                  <Text fontSize="xs" color="gray.500" fontWeight="600">Step {i + 1}</Text>
                  <Text fontSize="xs" color="gray.400">{s.pct_of_total}%</Text>
                </Flex>
                <Text fontSize="13px" fontWeight="600" color="gray.800" noOfLines={1} mb={2}>{s.label}</Text>
                <Flex align="flex-end" h="190px">
                  <Box w="60%" bg="var(--brand-primary)" borderTopRadius="4px" h={`${Math.max(h, 2)}px`} opacity={0.9} />
                </Flex>
                <Text fontSize="20px" fontWeight="700" color="gray.900" mt={2} sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(s.users)}</Text>
                {i < steps.length - 1 && (
                  <Text fontSize="11px" color="gray.500" mt={1}>
                    Abandonment <Text as="span" color={s.abandonment_rate > 50 ? 'red.500' : 'gray.700'} fontWeight="600">{s.abandonment_rate}%</Text>
                  </Text>
                )}
              </Box>
            );
          })}
        </SimpleGrid>

        {/* Breakdown table */}
        <Flex align="center" justify="space-between" mt={4} mb={2}>
          <Select size="sm" w="auto" value={breakdown} onChange={(e) => setBreakdown(e.target.value)}>
            {BREAKDOWNS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </Select>
          <Text fontSize="xs" color="gray.400">User count per step</Text>
        </Flex>
        {rows.length === 0 ? (
          <EmptyState label="No breakdown data for this range" minimal />
        ) : (
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th color="gray.500">{BREAKDOWNS.find((b) => b.value === breakdown)?.label}</Th>
                  {steps.map((s, i) => <Th key={i} isNumeric color="gray.500">{i + 1}. {s.label}</Th>)}
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((r, ri) => (
                  <Tr key={ri} _hover={{ bg: 'gray.50' }}>
                    <Td fontWeight="500" noOfLines={1}>{fmtSeg(r.seg)}</Td>
                    {r.steps.map((u, ci) => (
                      <Td key={ci} isNumeric fontFamily="mono" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(u)}</Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </ReportFrame>
  );
}

export default JourneyReport;
